import { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react'
import publicConfig from '@/config/public'
import { PayrixCountryEnum, PayrixSessionResponse, createPayrixSession } from '../apiClient'
import { getCurrencyByCountry } from '../utils'

export function usePayrixSession(
  venueXRefID: string | undefined,
  useStaging: boolean = false
): {
  payrixSDKLoaded: boolean
  payrixSessionToken: MutableRefObject<string>
  payrixSession: PayrixSessionResponse | undefined
  getPayrixSessionCurrency: (session?: PayrixSessionResponse) => string | undefined
  payrixScriptRef: RefObject<HTMLDivElement>
} {
  const [payrixSession, setPayrixSession] = useState<PayrixSessionResponse | undefined>()
  const payrixScriptRef = useRef<HTMLDivElement>(null)
  const [payrixSDKLoaded, setPayrixSDKLoaded] = useState(false)
  const payrixSessionToken = useRef<string>('')

  // Insert Payrix PayFields scripts and create a Payrix Session
  useEffect(() => {
    if (venueXRefID) {
      createPayrixSession(venueXRefID).then((sessionResponse) => {
        if (sessionResponse) {
          setPayrixSession(sessionResponse)
        }

        const script = document.createElement('script')
        if (useStaging) {
          if (sessionResponse.country === PayrixCountryEnum.CAN) {
            script.src = publicConfig.payrixPayFieldsTestCA_URL
          } else {
            script.src = publicConfig.payrixPayFieldsTestUS_URL
          }
        } else {
          if (sessionResponse.country === PayrixCountryEnum.CAN) {
            script.src = publicConfig.payrixPayFieldsCA_URL
          } else {
            script.src = publicConfig.payrixPayFieldsUS_URL
          }
        }

        const handleLoad = (): void => {
          // Flag the Payrix SDK as loaded
          setPayrixSDKLoaded(true)

          PayFields.config.sessionKey = sessionResponse.sessionKey
          PayFields.config.merchant = sessionResponse.merchantXRefID

          PayFields.config.amount = '0'
          PayFields.config.mode = 'token'
          PayFields.config.txnType = 'token'
        }

        // Attach 'load' event listener
        script.addEventListener('load', handleLoad)

        // Append the script element to the document's body to initiate loading
        payrixScriptRef.current?.appendChild(script)

        // Clean up event listener
        return () => {
          script.removeEventListener('load', handleLoad)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueXRefID])

  return {
    payrixSDKLoaded,
    payrixSessionToken,
    payrixSession,
    payrixScriptRef,
    getPayrixSessionCurrency: (session?: PayrixSessionResponse) => {
      if (session?.country === PayrixCountryEnum.CAN) {
        return getCurrencyByCountry('CA')
      } else if (session?.country === PayrixCountryEnum.USA) {
        return getCurrencyByCountry('US')
      }
    },
  }
}
